import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { AuthedApi, GuestApi } from "../../api/Api";
import { GetRoutes } from "./Pages";
import { useEffect, useState } from "react";
import useAuthedApi from "../../api/useAuthedApi";
import { syncMastersFromServer } from "../../api/dbSlice";
import LoginPage from "./LoginPage";
import { Stack, Box } from "@mui/material";
import PageHeader from "../../components/Header";
import { Action, VoidAction } from "../../util/func";
import { LoginSelectForUnAutholizedPage } from "../../components/LoginSelect";
import { useNotifyDialogDialog } from "../../components/dialog/NotifyDialog";
import { FullSizeLoading } from "../../components/Loading";
import { HomeButton } from "../../components/FunctionalButton";
import useNavigateParams from "../../util/navigate";

const PageLogger: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // ページ遷移時にログを出力
    console.log(`[PAGE VIEW] ${location.pathname}${location.search}`);
  }, [location]);

  return null;
};

export function RouteWrapper({ originalPath }: { originalPath?: string }) {
  const navigate = useNavigateParams();

  const [errors, setErrors] = useState<string[]>([]);
  const [api, updateApi] = useAuthedApi((e) => setErrors([...errors, e]));
  const [notifyDialog, showNotifyDialog] = useNotifyDialogDialog();

  useEffect(() => {
    if (api && originalPath) {
      navigate(originalPath);
    }
  }, [api]);

  // ログイン/ユーザー切替でオンラインの認証済 API が用意できたら、サーバからマスタを取得して
  // ローカル(IndexedDB)へ保存/更新する。マスタはサーバ→クライアントの取得経路が無かったため、
  // 他データ同様にここで同期する(完了時に invalidateStores 経由でマスタ画面が自動再描画される)。
  useEffect(() => {
    if (api && api.isOnline) {
      syncMastersFromServer(api, api.indexedDB, api.currentUser.id);
    }
  }, [api]);

  useEffect(() => {
    if (errors.length > 0) {
      showNotifyDialog({ title: "エラー", text: errors.join("\n\n"), onClose: () => setErrors([])});
    }
  }, [errors]);

  if (api === undefined) {
    return <FullSizeLoading message="Apiをロード中です。" />;
  }

  return (
    <Stack height="100%">
      <PageLogger />
      <Box flex={1} overflow="auto">
        <Routes>
          <Route path="login/" element={<LoginPageElement api={api} updateApi={updateApi} originalPath={originalPath} showError={(e) => setErrors([...errors, e])} />} />
          <Route path="*" element={<RequireAuth api={api} />}>
            {api && GetRoutes({ api: api, update: updateApi })}
          </Route>
        </Routes>
      </Box>
      {notifyDialog}
    </Stack>
  );
}

// Apiが認証済(現在のユーザーが設定されている)
function RequireAuth({ api }: { api: AuthedApi | null }) {
  const location = useLocation();
  if (api === null || !api.currentUser) {
    return <Navigate to="/console/login/" replace state={{ from: location }} />;
  }
  return <Outlet context={{api}} />;
}

function LoginPageElement({ api, updateApi, originalPath, showError } : { api: AuthedApi | null, updateApi: VoidAction, originalPath?: string, showError: Action<string> }) {
  const navigate = useNavigateParams();

  return (
    <>
      <PageHeader right={
        <Stack direction="row" justifyItems="center" alignItems="center" minWidth={0} gap={1} pr={2}>
          <LoginSelectForUnAutholizedPage updateApi={updateApi} />
          <HomeButton onBackHome={updateApi} />
        </Stack>
        }
        rightConfig={{ priority: 100, minWidth: 460 }}
      />
      <LoginPage
        login={async ({ username, password }) => {
          const api = new GuestApi();
          const success = await api.Login(username, password, showError);
          if (success) {
            updateApi();
            navigate(originalPath ?? "/console/");
          }
        }}
      />
    </>
  );
}